import React, { useState, useEffect } from "react";
import { IInputFilesModalProps } from "./definitions";
import { FileMetadata } from "../input-files/definitions";
import styles from "./style.module.scss";
import InputFiles from "components/input-files/InputFiles";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Button from "@amzn/awsui-components-react/polaris/button";
import Box from "@amzn/awsui-components-react/polaris/box";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Checkbox from "@amzn/awsui-components-react/polaris/checkbox";
import { useAppSelector } from "app/store/hooks";
import { validateFileMetadata } from "utils/input-files-utils";

const InputFilesModal: React.FC<IInputFilesModalProps> = (
  props: IInputFilesModalProps
) => {
  const teamName: string = useAppSelector((state) => state.audits.teamName);
  const [files, setFiles] = useState<File[]>([]);
  const [fileMetadata, setFileMetadata] = useState<FileMetadata[]>([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState<Set<string>>(new Set());

  // Duplicate detection: compare file names against existing documents
  useEffect(() => {
    // Only run when enableReplaceMode is true
    if (!props.enableReplaceMode || !props.existingDocuments) {
      return;
    }

    const duplicates = new Set<string>();

    // Compare each file name against existing documents (case-sensitive exact match)
    files.forEach((file) => {
      const isDuplicate = props.existingDocuments!.some((doc) => {
        // For Atlas documents, fileName doesn't include extension, so reconstruct full name
        const fullFileName = doc.fileName + (doc.extentionType || "");
        return fullFileName === file.name;
      });
      if (isDuplicate) {
        duplicates.add(file.name);
      }
    });

    setDuplicateFiles(duplicates);
  }, [files, props.existingDocuments, props.enableReplaceMode]);

  const onDismiss = (e: any) => {
    if (e.detail.reason == "overlay") return;
    resetModal();
  };

  const onCancelSave = () => resetModal();

  const onCreate = (e: any) => {
    // Validate files and metadata based on enabled features
    if (props.enableRenaming || props.enableDocumentTypes) {
      const validation = validateFileMetadata(
        fileMetadata,
        props.requireDocumentTypes
      );

      if (!validation.isValid) {
        setFileError(true);
        return;
      }

      setIsDisabled(true);
      if (props.onUpload) {
        props.onUpload(fileMetadata, () => {
          resetModal();
        });
      }
    } else {
      if (files.length === 0) {
        setFileError(true);
        return;
      }

      setIsDisabled(true);
      if (props.onCreate) {
        props.onCreate(
          files,
          teamName,
          () => {
            resetModal();
          },
          replaceExisting,
          props.existingDocuments || []
        );
      }
    }
  };

  const resetModal = () => {
    props.setVisible(false);
    setFiles([]);
    setFileMetadata([]);
    setFileError(false);
    setIsDisabled(false);
    setReplaceExisting(false);
    setDuplicateFiles(new Set());
  };

  const handleFileMetadataChange = (metadata: FileMetadata[]) => {
    setFileMetadata(metadata);
    setFileError(false);
  };

  const getErrorMessage = () => {
    if (!fileError) return undefined;
    if (props.enableRenaming || props.enableDocumentTypes) {
      const validation = validateFileMetadata(
        fileMetadata,
        props.requireDocumentTypes
      );
      return validation.errorMessage;
    } else {
      return "Please select files";
    }
  };

  return (
    <Modal
      visible={props.visible}
      onDismiss={onDismiss}
      header={props.title || "Upload files"}
      data-testid="input-files-modal"
      size={
        props.enableRenaming || props.enableDocumentTypes ? "large" : "medium"
      }
    >
      <div className={styles.modalContent}>
        <div className={styles.modalBody}>
          {props.enableRenaming || props.enableDocumentTypes ? (
            <div>
              {props.description || "Gaia keeps files for 10 years."}
              <br />
              <br />
            </div>
          ) : null}

          <InputFiles
            setFiles={setFiles}
            files={files}
            enableRenaming={props.enableRenaming}
            enableDocumentTypes={props.enableDocumentTypes}
            requireDocumentTypes={props.requireDocumentTypes}
            onFileMetadataChange={handleFileMetadataChange}
            fileMetadata={fileMetadata}
            acceptedFileTypes={props.acceptedFileTypes}
            documentTypeOptions={props.documentTypeOptions}
            defaultDocumentType={props.defaultDocumentType}
            gaiaObjectType={props.gaiaObjectType}
          />

          {duplicateFiles.size > 0 && (
            <div style={{ marginTop: "16px" }}>
              <Checkbox
                checked={replaceExisting}
                onChange={({ detail }) => setReplaceExisting(detail.checked)}
              >
                Replace existing files with the same name ({duplicateFiles.size}{" "}
                file{duplicateFiles.size > 1 ? "s" : ""})
              </Checkbox>
            </div>
          )}

          {fileError && (
            <div
              style={{ color: "#d13212", marginTop: "8px", fontSize: "14px" }}
            >
              {getErrorMessage()}
            </div>
          )}
        </div>
        <div className={styles.modalFooter}>
          <Box>
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                formAction="none"
                onClick={onCancelSave}
                variant="link"
                disabled={isDisabled}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={
                  isDisabled ||
                  (files.length === 0 && fileMetadata.length === 0)
                }
                onClick={onCreate}
              >
                {isDisabled ? <Spinner /> : null}
                {"Upload"}
              </Button>
            </SpaceBetween>
          </Box>
        </div>
      </div>
    </Modal>
  );
};

export default InputFilesModal;
